import { LangfuseWeb } from 'langfuse';

import config from '../config';

import { sendException } from './sentry';

// Initialize LangfuseWeb client
const langfuseWeb = new LangfuseWeb({
  publicKey: config.LANGFUSE_PUBLIC_KEY,
  baseUrl: config.LANGFUSE_BASE_URL,
});

/**
 * Generate a unique trace ID for feedback tracking
 *
 * @param context - The feedback context (e.g., 'narrative', 'claim-attachment', etc.)
 * @param estimatedVisitId - The estimated visit ID
 * @param unifiedAppointmentId - The unified appointment ID
 * @returns A unique trace ID string
 */
export const generateFeedbackTraceId = (
  context: string,
  estimatedVisitId: string,
  unifiedAppointmentId: string,
): string => {
  return `${context}-feedback-${estimatedVisitId}-${unifiedAppointmentId}-${Date.now()}`;
};

/**
 * Track user feedback to Langfuse
 *
 * @param traceId - The trace ID for the feedback
 * @param rating - The user's rating ('positive' or 'negative')
 * @param selectedOptions - Array of selected feedback options
 * @param additionalComments - Any additional comments from the user
 * @param context - The feedback context (e.g., 'narrative', 'claim-attachment', etc.)
 * @param metadata - Additional metadata to include
 */
export const trackFeedback = async (
  traceId: string,
  rating: 'positive' | 'negative',
  selectedOptions: string[],
  additionalComments?: string,
  context: string = 'general',
  metadata: Record<string, any> = {},
) => {
  try {
    // Convert rating to numeric score (1 for positive, 0 for negative)
    const score = rating === 'positive' ? 1 : 0;

    await langfuseWeb.score({
      traceId,
      name: `${context}_user_feedback`,
      value: score,
      comment: additionalComments || undefined,
      metadata: {
        selectedOptions,
        rating,
        feedbackType: context,
        ...metadata,
      },
    });
  } catch (error) {
    console.error(`Failed to track ${context} feedback to Langfuse:`, error);
    sendException(error, {
      tags: [
        ['context', 'langfuse_feedback_tracking'],
        ['feedback_context', context],
      ],
    });
  }
};

/**
 * Helper function to track feedback with automatic trace ID generation
 *
 * @param context - The feedback context (e.g., 'narrative', 'claim-attachment', etc.)
 * @param rating - The user's rating ('positive' or 'negative')
 * @param selectedOptions - Array of selected feedback options
 * @param additionalComments - Any additional comments from the user
 * @param estimatedVisitId - The estimated visit ID
 * @param unifiedAppointmentId - The unified appointment ID
 * @param metadata - Additional metadata to include
 */
export const trackFeedbackWithAutoTraceId = async (
  context: string,
  rating: 'positive' | 'negative',
  selectedOptions: string[],
  additionalComments: string | undefined,
  estimatedVisitId: string,
  unifiedAppointmentId: string,
  metadata: Record<string, any> = {},
) => {
  try {
    if (!estimatedVisitId || !unifiedAppointmentId) {
      console.warn(`Missing required IDs for ${context} feedback tracking`);
      return;
    }

    const traceId = generateFeedbackTraceId(
      context,
      estimatedVisitId,
      unifiedAppointmentId,
    );
    await trackFeedback(
      traceId,
      rating,
      selectedOptions,
      additionalComments,
      context,
      metadata,
    );
  } catch (error) {
    console.error(
      `Failed to track ${context} feedback with auto trace ID:`,
      error,
    );
    sendException(error, {
      tags: [
        ['context', 'langfuse_feedback_tracking_auto_trace'],
        ['feedback_context', context],
      ],
    });
  }
};

export { langfuseWeb };
